import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const CATS = [["", "All"], ["forex", "Forex"], ["metal", "Metals"], ["crypto", "Crypto"], ["index", "Indices"], ["energy", "Energy"]];
const SESS = [["", "Any session"], ["asia", "Asia"], ["london", "London"], ["ny", "New York"], ["custom", "Custom"]];
const DIRS = [["", "Any"], ["long", "Long"], ["short", "Short"]];

export default function CommunityPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ pair_category: "", strategy: "", session: "", direction: "" });

  const load = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
    const { data } = await api.get(`/community${q ? "?" + q : ""}`);
    setRows(data.trades);
    setStrategies(data.strategies);
    setLoading(false);
  }, [f]);
  useEffect(() => { load(); }, [load]);

  return (
    <Layout>
      <div className="mb-8 fade-in">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#CFA66A] mb-2">— Public trade feed</p>
        <h1 className="font-head text-4xl sm:text-5xl font-bold text-[#EBE5D9]">Community</h1>
        <p className="text-[#A39A8E] mt-3 max-w-2xl">Real trades from public traders. Filter by pair type, strategy, session, or direction to see how others approach the market.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { k: "pair_category", label: "Pair type", opts: CATS },
          { k: "session", label: "Session", opts: SESS },
          { k: "direction", label: "Position", opts: DIRS },
        ].map((x) => (
          <div key={x.k}>
            <label className="font-mono text-[9px] uppercase tracking-widest text-[#A39A8E]">{x.label}</label>
            <select data-testid={`filter-${x.k}`} value={f[x.k]} onChange={(e) => setF({ ...f, [x.k]: e.target.value })} className="mt-1 w-full bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-3 py-2 rounded-sm focus:outline-none focus:border-[#CFA66A] font-mono text-sm">
              {x.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        ))}
        <div>
          <label className="font-mono text-[9px] uppercase tracking-widest text-[#A39A8E]">Strategy</label>
          <select data-testid="filter-strategy" value={f.strategy} onChange={(e) => setF({ ...f, strategy: e.target.value })} className="mt-1 w-full bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-3 py-2 rounded-sm focus:outline-none focus:border-[#CFA66A] font-mono text-sm">
            <option value="">Any strategy</option>
            {strategies.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {loading ? <p className="text-center text-[#A39A8E] py-16 font-mono text-sm">Loading…</p> : rows.length === 0 ? (
        <div className="border border-dashed border-[#2E2A25] p-10 text-center rounded-sm text-[#A39A8E]">No trades match these filters yet.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((t, i) => (
            <div key={i} data-testid={`community-trade-${i}`} className="border border-[#2E2A25] bg-[#1A1815] p-5 rounded-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                  <Link to={`/u/${t.trader?.handle}`} className="font-head text-lg text-[#EBE5D9] hover:text-[#CFA66A]">{t.trader?.name || "Anon"}</Link>
                  <span className="font-mono text-xs text-[#A39A8E] ml-2">@{t.trader?.handle} · {t.date}</span>
                </div>
                <p className={`font-mono text-xl ${t.pnl > 0 ? "text-[#7A9E80]" : t.pnl < 0 ? "text-[#B56258]" : ""}`}>{formatMoney(t.pnl, user?.currency)}</p>
              </div>
              <p className="font-mono text-sm text-[#EBE5D9] mt-2">
                <span className="text-[#CFA66A]">{t.pair}</span> {String(t.direction).toUpperCase()} · {t.lot_size} lots
                {t.session && <span className="text-[#A39A8E]"> · {t.session.toUpperCase()}{t.session_time ? ` ${t.session_time}` : ""}</span>}
                {t.strategy && <span className="text-[#A39A8E]"> · {t.strategy}</span>}
                {t.outcome && <span className={`ml-2 ${t.outcome === "win" ? "text-[#7A9E80]" : t.outcome === "loss" ? "text-[#B56258]" : "text-[#5C7C8A]"}`}>[{t.outcome}]</span>}
              </p>
              {t.summary && <p className="text-sm text-[#EBE5D9] mt-3 whitespace-pre-wrap leading-relaxed">{t.summary}</p>}
              {t.share_token && (
                <Link to={`/share/${t.share_token}`} className="mt-3 inline-block font-mono text-[10px] uppercase tracking-widest text-[#CFA66A] hover:text-[#EBE5D9]">View full day →</Link>
              )}
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
